import { CanActivate, CanActivateChild, Router } from '@angular/router';
import { Inject, Injectable } from '@angular/core';

import { AuthService, IUserService } from '../services';
import { CORE_CONFIG } from '../tokens';
import { CoreConfig } from '../core.config';
import { firstValueFrom } from 'rxjs';

@Injectable()
export class CanActivateIfAuthGuard implements CanActivate, CanActivateChild {
  constructor(
    private authService: AuthService,
    private router: Router,
    @Inject(CORE_CONFIG) private config: CoreConfig,
    private userService: IUserService,
  ) {}

  async canActivate() {
    if (await this.authService.isAuthenticated) {
      try {
        const user = await firstValueFrom(this.userService.me());

        // Check if we're using a dummy user due to network/auth errors
        if (this.userService.isDummyUserActive()) {
          console.warn('Dummy user is active, attempting to refresh user data...');

          try {
            // Try to refresh user data without dummy fallback
            await this.userService.refreshUserData();
            return true;
          } catch (refreshError) {
            console.error('Failed to refresh user data, redirecting to auth:', refreshError);
            // If refresh fails, deauthenticate and redirect to login
            void this.authService.deauthenticate();
            await this.router.navigate(this.config.authRoute, {
              state: { error: 'Session expired or network error. Please login again.' },
            });
            return false;
          }
        }

        if (user) {
          return true;
        }
      } catch (error: unknown) {
        console.error('Authentication error in guard:', error);
        void this.authService.deauthenticate();
        await this.router.navigate(this.config.authRoute, {
          state: {
            error:
              error &&
              typeof error === 'object' &&
              'error' in error &&
              error.error &&
              typeof error.error === 'object' &&
              'message' in error.error
                ? (error.error.message as string)
                : "Vous n'avez pas accès à cette application",
          },
        });
        return false;
      }
    }

    return this.router.createUrlTree(this.config.authRoute);
  }

  canActivateChild() {
    return this.canActivate();
  }
}
